import { useState, useEffect, useCallback } from 'react';
import { Switch } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { MdOutlineDarkMode } from 'react-icons/md';
import { BsTelegram, BsPinterest } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import CreateChannelModal from '../Modals/CreateChannelModal';
import CircleAvatar from '../Channel/CircleAvatar';
import { useTheme } from '../../hooks/useTheme';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logout } from '../../redux/slices/authSlice';
import { logout as logoutAPI } from '../../api/auth';
import { toast } from 'react-toastify';
import {
    sidebarMenuOverlay,
    sidebarMenuContainer,
    menuItemButton,
    darkModeSwitchContainer,
    menuButton,
    menuButtonActive,
    menuButtonInactive
} from '../../styles/commonStyles';
import { ImagePlus, BookOpen, User, Users, Globe, FolderOpen, Radio, Archive, ShieldCheck, Settings, Crown, LogIn, LogOut, Images } from 'lucide-react';
import BookmarkIcon from '../Icons/BookmarkIcon';
import getIsAdmin from '../../utils/isAdmin';
import { useCollectionsByUserId } from '../../api/collections';
import { useUserLevel } from '../../hooks/useUserLevel';
import { useQuery } from 'react-query';
import { getSourceTypes } from '../../api/sources';

function SidebarMenu({ isOpen, onClose, onLoginClick, onCreateChannelClick, onOpen }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const { isTelegramApp, telegramUser } = useSelector((state) => state.telegram);
    const [isCreateModalOpen, setCreateModalOpen] = useState(false);
    const { darkMode, handleThemeChange, isTelegramControlled } = useTheme();
    const [isVisible, setIsVisible] = useState(isOpen);
    const [animateOut, setAnimateOut] = useState(false);
    const isAdminStatus = getIsAdmin(user, telegramUser);
    const { hasFeature } = useUserLevel();
    
    // Получаем каналы пользователя для поиска автоматического канала
    const { data: userChannels } = useCollectionsByUserId(user?.id);
    const defaultChannel = userChannels?.find(channel => channel.is_default);

    // Получаем количество открыток по типам источников (для скрытия пустых разделов)
    const { data: sourceTypesData } = useQuery('sourceTypes', getSourceTypes, {
        staleTime: 5 * 60 * 1000,
    });
    const getTypeImageCount = (type) => {
        const found = sourceTypesData?.types?.find(t => t.type === type);
        return found?.images_count || 0;
    };
    



    const handleLogout = useCallback(async () => {
        try {
            // Сначала делаем запрос на бэкенд
            await logoutAPI();
            
            // Если запрос успешен, очищаем состояние на фронтенде
            dispatch(logout());
            localStorage.removeItem('user');
            
            // Закрываем меню и перенаправляем на главную
            onClose();
            navigate('/');
            
            toast.success(t('auth.logoutSuccess'));
        } catch (error) {
            console.error('Logout error:', error);
            toast.error(t('auth.logoutError'));
        }
    }, [dispatch, navigate, onClose, t]);

    // Добавляем обработку свайпа влево для закрытия меню
    useEffect(() => {
        if (!isOpen) return;
        let touchStartX = null;
        let touchEndX = null;
        const minSwipeDistance = 50;
        function handleTouchStart(e) {
            if (e.touches && e.touches.length === 1) {
                touchStartX = e.touches[0].clientX;
            }
        }
        function handleTouchMove(e) {
            if (e.touches && e.touches.length === 1) {
                touchEndX = e.touches[0].clientX;
            }
        }
        function handleTouchEnd(e) {
            if (touchStartX !== null && touchEndX !== null) {
                const deltaX = touchStartX - touchEndX;
                if (deltaX > minSwipeDistance) {
                    onClose(); // свайп влево закрывает меню
                    e.preventDefault();
                }
            }
            touchStartX = null;
            touchEndX = null;
        }
        window.addEventListener('touchstart', handleTouchStart, { passive: false });
        window.addEventListener('touchmove', handleTouchMove, { passive: false });
        window.addEventListener('touchend', handleTouchEnd, { passive: false });
        return () => {
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isOpen, onClose]);

    // Добавляю глобальный обработчик свайпа слева направо для открытия меню
    useEffect(() => {
        if (isOpen) return;
        let touchStartX = null;
        let touchEndX = null;
        const minSwipeDistance = 50;
        function handleTouchStart(e) {
            if (e.touches && e.touches.length === 1) {
                touchStartX = e.touches[0].clientX;
            }
        }
        function handleTouchMove(e) {
            if (e.touches && e.touches.length === 1) {
                touchEndX = e.touches[0].clientX;
            }
        }
        function handleTouchEnd(e) {
            if (touchStartX !== null && touchEndX !== null) {
                const deltaX = touchEndX - touchStartX;
                if (deltaX > minSwipeDistance && touchStartX < 40) {
                    if (typeof onOpen === 'function') onOpen();
                    e.preventDefault();
                }
            }
            touchStartX = null;
            touchEndX = null;
        }
        window.addEventListener('touchstart', handleTouchStart, { passive: false });
        window.addEventListener('touchmove', handleTouchMove, { passive: false });
        window.addEventListener('touchend', handleTouchEnd, { passive: false });
        return () => {
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isOpen, onOpen]);

    // Управление видимостью для анимации
    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);
            setAnimateOut(false);
        } else if (isVisible) {
            setAnimateOut(true);
            const timeout = setTimeout(() => {
                setIsVisible(false);
                setAnimateOut(false);
            }, 350);
            return () => clearTimeout(timeout);
        }
    }, [isOpen]);

    if (!isVisible) return null;

    // AGR: Базовые пункты меню
    const baseMenuItems = [];

    // Пункт "Галерея" для всех
    const galleryMenuItem = {
        label: 'sidebar.gallery',
        fallback: 'Галерея',
        path: '/gallery',
        icon: <Images size={20} />
    };

    // Пункт "Авторы" для всех
    const authorsMenuItem = {
        label: 'sidebar.authors',
        fallback: 'Авторы',
        path: '/authors',
        icon: <Users size={20} />
    };

    // Пункт "Сайты" для всех
    const websitesMenuItem = {
        label: 'sidebar.websites',
        fallback: 'Сайты',
        path: '/sources/websites',
        icon: <Globe size={20} />
    };

    // Пункт "Telegram" (скрыть если менее 10 открыток)
    const telegramSourcesMenuItem = getTypeImageCount('telegram') >= 10 ? {
        label: 'sidebar.telegram_sources',
        fallback: 'Telegram',
        path: '/sources/telegram',
        icon: <BsTelegram size={20} />
    } : null;

    // Пункт "Pinterest" (скрыть если менее 10 открыток)
    const pinterestSourcesMenuItem = getTypeImageCount('pinterest') >= 10 ? {
        label: 'sidebar.pinterest_sources',
        fallback: 'Pinterest',
        path: '/sources/pinterest',
        icon: <BsPinterest size={20} />
    } : null;

    // Пункт "Гостевая" для авторизованных пользователей
    const wallMenuItem = (isAuthenticated || (isTelegramApp && telegramUser)) ? {
        label: 'sidebar.wall',
        fallback: 'Гостевая',
        path: '/stone',
        icon: <BookOpen size={20} />
    } : null;

    // Пункт "Избранное" для авторизованных пользователей (если доступно по уровню)
    const bookmarksMenuItem = ((isAuthenticated || (isTelegramApp && telegramUser)) && hasFeature('BOOKMARKS_USE')) ? {
        label: 'sidebar.bookmarks',
        fallback: 'Избранное',
        path: '/bookmarks',
        icon: <BookmarkIcon size={20} />
    } : null;

    // ===== АДМИНСКИЕ ПУНКТЫ (только для isAdminStatus) =====

    // Пункт "Мои каналы" для админов
    const myChannelsMenuItem = isAdminStatus ? {
        label: 'sidebar.channels',
        fallback: 'Мои каналы',
        path: '/mychannels',
        icon: <Radio size={20} />
    } : null;

    // Пункт "Мой профиль" для админов с функцией MY_PROFILE
    const myProfileMenuItem = isAdminStatus && defaultChannel && hasFeature('MY_PROFILE') ? {
        label: 'sidebar.myProfile',
        fallback: 'Мой профиль',
        path: `/mychannels/${defaultChannel.id}`,
        icon: <User size={20} />
    } : null;

    // Пункт "Категории" для админов
    const myCategoriesMenuItem = isAdminStatus ? {
        label: 'sidebar.categories',
        fallback: 'Категории',
        path: '/categories',
        icon: <FolderOpen size={20} />
    } : null;

    // Пункт "Мой канал" для всех авторизованных пользователей
    const myUploadsMenuItem = {
        label: 'sidebar.myChannel',
        fallback: 'Мой канал',
        path: '/my-channel',
        icon: <ImagePlus size={20} />
    };

    // Пункт "Песочница" для админов
    const sandboxMenuItem = isAdminStatus ? {
        label: 'sidebar.sandbox',
        fallback: 'Песочница',
        path: '/sandbox',
        icon: <Archive size={20} />
    } : null;

    // Пункт "Панель модератора" для админов
    const moderatorMenuItem = isAdminStatus ? {
        label: 'sidebar.moderator',
        fallback: 'Панель модератора',
        path: '/moderator',
        icon: <Crown size={20} className="text-yellow-500" />
    } : null;

    // Пункт "Управление кешем" для админов
    const cacheManagementMenuItem = isAdminStatus ? {
        label: 'sidebar.cacheManagement',
        fallback: 'Управление кешем',
        path: '/cache-management',
        icon: <Settings size={20} />
    } : null;

    // Пункт "Панель администратора" только для is_admin
    const adminPanelMenuItem = (user?.is_admin) ? {
        label: 'sidebar.adminPanel',
        fallback: 'Администратор',
        path: '/admin',
        icon: <Crown size={20} className="text-red-500" />
    } : null;

    // Пункт "Настройки" для админов
    const settingMenuItem = isAdminStatus ? {
        label: 'sidebar.settings',
        fallback: 'Настройки',
        path: '/settings',
        icon: <Settings size={20} />
    } : null;

    // Пункт "Создание канала" для админов
    const createChannelMenuItem = isAdminStatus ? {
        label: 'sidebar.createChannel',
        fallback: 'Создать канал',
        action: () => {
            onCreateChannelClick();
            onClose();
        },
        icon: <Radio size={20} />
    } : null;

    // AGR: Меню для авторизованных пользователей
    // Админы видят всё, обычные пользователи — только галерею и закладки
    const authMenuItems = [
        galleryMenuItem,
        authorsMenuItem,
        websitesMenuItem,
        telegramSourcesMenuItem,
        pinterestSourcesMenuItem,
        ...(myProfileMenuItem ? [myProfileMenuItem] : []),
        ...(myChannelsMenuItem ? [myChannelsMenuItem] : []),
        ...(myCategoriesMenuItem ? [myCategoriesMenuItem] : []),
        myUploadsMenuItem,
        ...(sandboxMenuItem ? [sandboxMenuItem] : []),
        ...(wallMenuItem ? [wallMenuItem] : []),
        ...(bookmarksMenuItem ? [bookmarksMenuItem] : []),
        ...(moderatorMenuItem ? [moderatorMenuItem] : []),
        ...(cacheManagementMenuItem ? [cacheManagementMenuItem] : []),
        ...(adminPanelMenuItem ? [adminPanelMenuItem] : []),
        ...(settingMenuItem ? [settingMenuItem] : []),
        ...(createChannelMenuItem ? [createChannelMenuItem] : []),
        // Кнопка "Выход" только для обычных авторизованных пользователей (не Telegram)
        ...(isAuthenticated && !telegramUser ? [{ label: 'sidebar.logout', fallback: 'Выйти', action: handleLogout, icon: <LogOut size={20} /> }] : [])
    ].filter(Boolean);

    // Пункты меню для неавторизованных пользователей (только для обычного сайта)
    const guestMenuItems = isTelegramApp ? [] : [
        { 
            label: 'sidebar.login',
            fallback: 'Войти',
            action: () => {
                onLoginClick();
                onClose();
            }, 
            icon: <LogIn size={20} /> 
        },
    ];

    // Определяем, авторизован ли пользователь (через обычную авторизацию или Telegram)
    const isUserAuthenticated = isAuthenticated || (isTelegramApp && telegramUser);

    
    
    
    
    


    // Формируем меню в зависимости от контекста
    const menuItems = isUserAuthenticated 
        ? [...baseMenuItems, ...authMenuItems]
        : [...baseMenuItems, ...guestMenuItems];


    return (
        <div className={sidebarMenuOverlay} onClick={onClose}>
            <div className={`${sidebarMenuContainer} ${animateOut ? 'animate-slideOutRight' : 'animate-slideInRight'} flex flex-col`} onClick={(e) => e.stopPropagation()}>
                <div className="flex-1 overflow-y-auto">
                    {menuItems.filter(Boolean).map((item, idx) => (
                        <button
                            key={idx}
                            className={`${menuButton} ${item.path && location?.pathname?.startsWith(item.path) ? menuButtonActive : menuButtonInactive}`}
                            onClick={() => {
                                onClose();
                                if (item.action) {
                                    item.action();
                                } else if (item.path) {
                                    navigate(item.path);
                                }
                            }}
                        >
                            <span className="flex items-center gap-3">
                                {item.icon}
                                <span>{t(item.label, item.fallback || item.label)}</span>
                            </span>
                        </button>
                    ))}
                </div>
                {typeof __BUILD_TIME__ !== 'undefined' && (
                    <div className="text-center text-[9px] text-gray-300 dark:text-gray-600 mt-3 pb-2">
                        v{__BUILD_TIME__.slice(0, 16).replace('T', ' ')}
                    </div>
                )}
            </div>
        </div>
    );
}

export default SidebarMenu;
